import React from 'react';
import styled from 'styled-components';
import { Switch } from '@material-ui/core';
import Imgix from 'shared/components/imgix';
import {
  CheckoutLabelGrouping,
  CheckoutLabel,
  CheckoutBody,
  CheckoutSectionHeading,
  CheckoutSection,
} from 'checkout/components/styles/common';
import useCart from 'hooks/use-cart';
import { useRoundupForCharity } from './use-roundup-for-charity';

export const RoundupForCharity = (): JSX.Element | null => {
  const { activeCharity, shouldShow, roundupAmount, charityRoundupSelected } = useRoundupForCharity();
  const Cart = useCart();

  if (!shouldShow) {
    return null;
  }
  return (
    <CheckoutSection>
      <CheckoutLabelGrouping id='charity'>
        <CheckoutLabel>
          <CheckoutSectionHeading id='charitySectionHeading'>Make an impact</CheckoutSectionHeading>
        </CheckoutLabel>

        <CheckoutBody>
          <CharityContainer>
            {activeCharity && (
              <>
                <RoundupHeader>
                  <ToggleContainer>
                    <StyledSwitch
                      checked={charityRoundupSelected}
                      onChange={(event) => Cart.setCharityRoundupSelected(event.target.checked)}
                    />
                    Round up for:
                  </ToggleContainer>
                  <Imgix
                    src={activeCharity.logoUrl}
                    htmlAttributes={{ alt: `${activeCharity.name} Logo` }}
                    width={65}
                  />
                </RoundupHeader>
                <RoundupDescription>
                  Round up your order by donating <b>${roundupAmount.toFixed(2)}</b> to{' '}
                  <CharityLink target='_blank' href={activeCharity.websiteUrl}>
                    {activeCharity.name}
                  </CharityLink>
                  . {activeCharity.description}
                </RoundupDescription>
              </>
            )}
          </CharityContainer>
        </CheckoutBody>
      </CheckoutLabelGrouping>
    </CheckoutSection>
  );
};

const CharityContainer = styled.div`
  display: flex;
  max-width: 390px;
  padding: ${({ theme }) => theme.spaces[5]};
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spaces[5]};
  flex: 1 0 0;
  border-radius: 5px;
  border: 1px solid #d8dee3;
  background: ${({ theme }) => theme.colors.white};
`;

const RoundupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spaces[2]};
  align-self: stretch;
`;

const RoundupDescription = styled.p`
  color: ${({ theme }) => theme.colors.grey[30]};
  font-family: ${({ theme }) => theme.customized.fonts.secondary};
  font-size: 13px;
  line-height: 18px;
  color: #646d72;
`;

const CharityLink = styled.a`
  text-decoration: underline;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spaces[3]};
  flex: 1 0 0;
  color: ${({ theme }) => theme.colors.grey[30]};
  font-family: ${({ theme }) => theme.customized.fonts.secondary};
  font-size: 14px;
  font-weight: bold;
`;

const StyledSwitch = styled(Switch)`
  &.MuiSwitch-root {
    width: 40px;
    height: 24px;
    border-radius: 14px;
    padding: 0px;

    .MuiSwitch-switchBase.Mui-checked {
      transform: translateX(16px);
    }

    .MuiSwitch-track {
      background-color: ${({ theme }) => theme.colors.grey[75]};
      opacity: 1;
    }
    .Mui-checked + .MuiSwitch-track {
        background-color: ${({ theme }) => theme.customized.colors.buttonsLinks};
        opacity: 1;
      }
    }

    .MuiSwitch-colorSecondary.Mui-checked:hover {
      background: none;
    }
    .MuiButtonBase-root {
      padding: 2px;
    }
    .MuiSwitch-thumb {
      background-color: #fff;
      height: 20px;
      width: 20px;
      box-shadow: none;
    }
    .Mui-checked {
      .MuiSwitch-thumb {
        background-color: #fff;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 16px;
          height: 16px;
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M12.2068 4.79279C12.3945 4.98031 12.5 5.23462 12.5 5.49979C12.5 5.76495 12.3945 6.01926 12.2068 6.20679L7.19985 11.2068C7.01206 11.3943 6.7574 11.4996 6.49187 11.4996C6.22634 11.4996 5.97167 11.3943 5.78388 11.2068L3.7811 9.20679C3.59869 9.01818 3.49776 8.76558 3.50004 8.50339C3.50232 8.24119 3.60763 7.99038 3.7933 7.80497C3.97897 7.61956 4.23013 7.51439 4.49269 7.51211C4.75525 7.50983 5.0082 7.61063 5.19707 7.79279L6.49187 9.08579L10.7908 4.79279C10.9786 4.60532 11.2333 4.5 11.4988 4.5C11.7644 4.5 12.019 4.60532 12.2068 4.79279Z' fill='white'/%3E%3C/svg%3E");
          mask-size: contain;
          mask-repeat: no-repeat;
          mask-position: center;
          background-color: ${({ theme }) => theme.customized.colors.buttonsLinks};
        }
      }
    }
  }
`;
